// crypto.ts - Browser-side end-to-end encryption for CrossClip.
//
// Security model:
//   - roomSecret: 256-bit random, generated in-browser, NEVER sent to server.
//     Lives in URL fragment (#k=...) and sessionStorage.
//   - itemKey: derived per-item via HKDF-SHA-256 from roomSecret.
//   - AES-256-GCM with random 12-byte IV per encryption.
//   - AAD binds roomId, itemId, kind, and protocol version.
//
// The server only ever sees: itemId, roomId, ciphertext, IV, AAD.
// It cannot decrypt without roomSecret.

const PROTOCOL_VERSION = 1;

// base64url encode/decode helpers.
function bytesToBase64(bytes: ArrayBuffer | Uint8Array): string {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let bin = '';
  for (let i = 0; i < b.length; i++) bin += String.fromCharCode(b[i]);
  return btoa(bin);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

// Generate a 256-bit roomSecret as 32 random bytes, returned as base64url.
export function generateRoomSecret(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  // base64url encoding (no padding)
  return bytesToBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Import roomSecret as a CryptoKey for HKDF.
async function importRoomSecret(roomSecret: string): Promise<CryptoKey> {
  // base64url decode
  const b64 = roomSecret.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = base64ToBytes(padded);
  // Copy into a fresh ArrayBuffer to satisfy BufferSource typing
  // (Uint8Array.buffer can be SharedArrayBuffer which is incompatible).
  const buf = new ArrayBuffer(raw.length);
  new Uint8Array(buf).set(raw);
  return crypto.subtle.importKey('raw', buf, 'HKDF', false, ['deriveKey', 'deriveBits']);
}

// Derive a per-item AES-256-GCM key from roomSecret.
// info = "item:" + itemId, salt = roomId (as UTF-8 bytes).
export async function deriveItemKey(roomSecret: string, roomId: string, itemId: string): Promise<CryptoKey> {
  const baseKey = await importRoomSecret(roomSecret);
  const salt = new TextEncoder().encode(roomId);
  const info = new TextEncoder().encode('item:' + itemId);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false, // not extractable
    ['encrypt', 'decrypt'],
  );
}

// AAD structure: {v, roomId, itemId, kind}. Serialized as UTF-8 JSON.
export interface AADPayload {
  v: number;
  roomId: string;
  itemId: string;
  kind: string;
}

function encodeAAD(payload: AADPayload): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(payload));
}

export interface EncryptionResult {
  ciphertext: string;   // base64
  nonce: string;        // base64, 12 bytes
  aad: string;          // base64
}

export interface DecryptionInput {
  ciphertext: string;
  nonce: string;
  aad: string;
}

// Encrypt plaintext with the given key. AAD is bound to the ciphertext
// for integrity verification.
export async function encrypt(
  key: CryptoKey,
  plaintext: string,
  aadPayload: AADPayload,
): Promise<EncryptionResult> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plaintext);
  const aad = encodeAAD({ ...aadPayload, v: PROTOCOL_VERSION });

  const cipherBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource, additionalData: aad as BufferSource },
    key,
    encoded as BufferSource,
  );

  return {
    ciphertext: bytesToBase64(cipherBuf),
    nonce: bytesToBase64(iv),
    aad: bytesToBase64(aad),
  };
}

// Decrypt ciphertext with the given key. Returns the plaintext string.
// Throws if the key is wrong, the ciphertext was tampered with, or the
// AAD does not match.
export async function decrypt(
  key: CryptoKey,
  input: DecryptionInput,
): Promise<string> {
  const cipher = base64ToBytes(input.ciphertext);
  const iv = base64ToBytes(input.nonce);
  const aad = base64ToBytes(input.aad);

  const plainBuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as BufferSource, additionalData: aad as BufferSource },
    key,
    cipher as BufferSource,
  );

  return new TextDecoder().decode(plainBuf);
}

// Generate a random item ID (128-bit, base32). Mirrors the server's
// idgen.NewRoomID format so IDs are consistent.
export function generateItemId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  // base32 (A-Z2-7) without padding
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let result = '';
  let bits = 0;
  let value = 0;
  for (let i = 0; i < bytes.length; i++) {
    value = (value << 8) | bytes[i];
    bits += 8;
    while (bits >= 5) {
      result += alphabet[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) {
    result += alphabet[(value << (5 - bits)) & 31];
  }
  return result;
}

export { PROTOCOL_VERSION };
